import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import muiTheme from './theme/muiTheme';
import { SnackbarProvider } from './context/SnackbarContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import AppLayout from './components/layout/AppLayout';
import Dashboard from './pages/Dashboard';
import MasterData from './pages/MasterData/index';
import PriceManagement from './pages/PriceManagement';
import ExcelImportExport from './pages/ExcelImportExport';
import HeaderConfiguration from './pages/HeaderConfiguration';
import Estimation from './pages/Estimation';
import EstimationHistory from './pages/EstimationHistory';
import UserManagement from './pages/admin/UserManagement';
import GroupManagement from './pages/admin/GroupManagement';
import AuditLog from './pages/admin/AuditLog';
import AccessoryPriceManagement from './pages/AccessoryPriceManagement';
import MaterialAccessories from './pages/MaterialAccessories';
import AccessoryExcelImportExport from './pages/AccessoryExcelImportExport';

// ─── Full-screen loading indicator ────────────────────────────────────────────
// Shown only when there is no cached user yet (first load / logged-out refresh).
// Once a stale user is available from localStorage, the content renders
// immediately while token verification runs in the background.
function FullScreenLoader() {
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', bgcolor: 'background.default',
      }}
    >
      <CircularProgress size={48} thickness={4} />
    </Box>
  );
}

// ─── Route guards ──────────────────────────────────────────────────────────────

// There's no login page here — Portal is the only place credentials are ever
// entered. An unauthenticated visit (or a stale/missing shared cookie) sends
// the browser to Portal's login via a real cross-origin redirect.
function LoginRedirect() {
  const portalUrl = process.env.REACT_APP_PORTAL_URL;
  if (portalUrl) {
    window.location.href = `${portalUrl}/login`;
    return <FullScreenLoader />;
  }
  return <Navigate to="/login" replace />;
}

// Requires authenticated user.
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading && !user) return <FullScreenLoader />;
  if (!user) return <LoginRedirect />;
  return children;
}

// Requires a specific permission (ADMIN always passes).
function PermissionRoute({ permission, children, redirectTo = '/prices' }) {
  const { user, loading, hasPermission } = useAuth();
  if (loading && !user) return <FullScreenLoader />;
  if (!user) return <LoginRedirect />;
  if (!hasPermission(permission)) return <Navigate to={redirectTo} replace />;
  return children;
}

// ─── App routes ───────────────────────────────────────────────────────────────
function AppRoutes() {
  const { user, loading } = useAuth();

  // No cached user at all — show full-screen spinner while auth resolves.
  // If there IS a stale user in localStorage, skip the spinner and let
  // ProtectedRoute / PermissionRoute render content immediately.
  if (loading && !user) return <FullScreenLoader />;

  return (
    <Routes>
      {/* All authenticated pages share a single AppLayout shell */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Routes>
                <Route
                  path="/"
                  element={
                    <PermissionRoute permission="VIEW_DASHBOARD" redirectTo="/prices">
                      <Dashboard />
                    </PermissionRoute>
                  }
                />
                <Route path="/master-data"         element={<MasterData />} />
                <Route path="/prices"              element={<PriceManagement />} />
                <Route path="/excel"               element={<ExcelImportExport />} />
                <Route
                  path="/header-config"
                  element={
                    <PermissionRoute permission="MANAGE_HEADER_CONFIG">
                      <HeaderConfiguration />
                    </PermissionRoute>
                  }
                />
                <Route path="/estimation"          element={<Estimation />} />
                <Route path="/estimation-history"  element={<EstimationHistory />} />
                <Route path="/accessory-prices"    element={<AccessoryPriceManagement />} />
                <Route path="/material-accessories" element={<MaterialAccessories />} />
                <Route path="/accessory-excel"     element={<AccessoryExcelImportExport />} />
                <Route
                  path="/admin/users"
                  element={<PermissionRoute permission="MANAGE_USERS"><UserManagement /></PermissionRoute>}
                />
                <Route
                  path="/admin/groups"
                  element={<PermissionRoute permission="MANAGE_PERMISSIONS"><GroupManagement /></PermissionRoute>}
                />
                <Route path="/admin/audit-logs"    element={<AuditLog />} />

                {/* Catch-all: unknown paths redirect to home rather than blank screen */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </AppLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <SnackbarProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </SnackbarProvider>
    </ThemeProvider>
  );
}
